/**
 * PrepaidAdminPanel · v2.7.0 batch DM
 * ====================================
 * Dispatch-side: manage prepaid customer accounts + assign trips to drivers.
 * Designed as a compact panel that can be embedded anywhere in the admin
 * console (we're mounting it at the top of BookRideTab so dispatchers see
 * prepaid customers alongside their dispatch queue).
 *
 * Three responsibilities:
 *   1. List prepaid-enabled customers with current balance + top-up control
 *   2. Create a new assignment (pick customer + pick driver)
 *   3. Show queued + in-progress assignments with cancel action
 */
import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Wallet, Plus, X, RefreshCw, AlertCircle, Trash2 } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";

export default function PrepaidAdminPanel() {
  const [customers, setCustomers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  // Top-up dialog state
  const [topupTarget, setTopupTarget] = useState(null);
  const [topupAmount, setTopupAmount] = useState("");
  const [topupBusy, setTopupBusy] = useState(false);
  // Assign dialog state
  const [assignCustomerId, setAssignCustomerId] = useState("");
  const [assignDriverId, setAssignDriverId] = useState("");
  const [assignNotes, setAssignNotes] = useState("");
  const [assignBusy, setAssignBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, a, d] = await Promise.all([
        adminApi.get("/admin/prepaid/customers"),
        adminApi.get("/admin/prepaid/assignments"),
        adminApi.get("/admin/drivers"),
      ]);
      setCustomers(c.data?.customers || []);
      setAssignments(a.data?.assignments || []);
      setDrivers((d.data?.drivers || d.data || []).filter((x) => x.active !== false));
    } catch (e) {
      toast.error("Could not load prepaid data");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const topup = async () => {
    if (!topupTarget) return;
    const cents = Math.round(parseFloat(topupAmount || "0") * 100);
    if (!Number.isFinite(cents) || cents <= 0) {
      toast.error("Enter a positive dollar amount");
      return;
    }
    setTopupBusy(true);
    try {
      await adminApi.put(`/admin/customers/${topupTarget.id}/prepaid`, { topup_cents: cents, note: `Top-up by admin · $${(cents/100).toFixed(2)}` });
      toast.success(`+ $${(cents/100).toFixed(2)} added to ${topupTarget.name}`);
      setTopupTarget(null); setTopupAmount("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Top-up failed");
    } finally { setTopupBusy(false); }
  };

  const togglePrepaid = async (cust) => {
    try {
      await adminApi.put(`/admin/customers/${cust.id}/prepaid`, { prepaid_enabled: !cust.prepaid_enabled });
      toast.success(`Prepaid ${cust.prepaid_enabled ? "disabled" : "enabled"} for ${cust.name}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Toggle failed");
    }
  };

  const assign = async () => {
    if (!assignCustomerId || !assignDriverId) {
      toast.error("Pick a customer AND a driver");
      return;
    }
    setAssignBusy(true);
    try {
      await adminApi.post("/admin/prepaid/assignments", {
        customer_id: assignCustomerId,
        driver_id: assignDriverId,
        notes: assignNotes.trim() || null,
      });
      toast.success("Prepaid trip queued to driver");
      setAssignCustomerId(""); setAssignDriverId(""); setAssignNotes("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Assignment failed");
    } finally { setAssignBusy(false); }
  };

  const cancelAssignment = async (id) => {
    if (!window.confirm("Cancel this prepaid trip assignment?")) return;
    try {
      await adminApi.delete(`/admin/prepaid/assignments/${id}`);
      toast.success("Assignment cancelled");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cancel failed");
    }
  };

  if (!open) {
    return (
      <Card className="bg-zinc-900 border-zinc-800 p-3 mb-3 flex items-center gap-3" data-testid="prepaid-admin-collapsed">
        <Wallet className="h-5 w-5 text-amber-400"/>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-zinc-100">Pre-Paid Accounts &amp; Assignments</div>
          <div className="text-xs text-zinc-500">Manage weekly-rate customer balances + pre-assign trips to a specific driver&apos;s queue.</div>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold" data-testid="prepaid-admin-open">Open</Button>
      </Card>
    );
  }

  return (
    <Card className="bg-zinc-900 border-amber-400/40 p-4 mb-3 space-y-4" data-testid="prepaid-admin-panel">
      <div className="flex items-center gap-3">
        <Wallet className="h-5 w-5 text-amber-400"/>
        <h3 className="flex-1 text-sm font-bold uppercase tracking-widest text-amber-300">Pre-Paid Accounts &amp; Assignments</h3>
        <Button variant="outline" size="sm" onClick={load} className="border-zinc-700 text-zinc-300" data-testid="prepaid-admin-refresh"><RefreshCw className="h-3.5 w-3.5"/></Button>
        <Button variant="ghost" size="sm" onClick={() => setOpen(false)} className="text-zinc-400" data-testid="prepaid-admin-close"><X className="h-4 w-4"/></Button>
      </div>

      {loading ? (
        <div className="text-center py-6"><Loader2 className="h-5 w-5 animate-spin inline text-zinc-400"/></div>
      ) : (
        <>
          {/* CREATE NEW ASSIGNMENT */}
          <Card className="bg-zinc-950 border-zinc-800 p-3 space-y-2" data-testid="prepaid-admin-assign-card">
            <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold flex items-center gap-1"><Plus className="h-3 w-3"/>Pre-Assign Trip to Driver Queue</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div>
                <Label className="text-[10px]">Customer (prepaid-enabled)</Label>
                <Select value={assignCustomerId} onValueChange={setAssignCustomerId}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-700 mt-1" data-testid="prepaid-admin-assign-customer"><SelectValue placeholder="Pick customer…"/></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-100 max-h-72">
                    {customers.filter((c) => c.prepaid_enabled).map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name} · ${(c.prepaid_balance_cents / 100).toFixed(2)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px]">Driver</Label>
                <Select value={assignDriverId} onValueChange={setAssignDriverId}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-700 mt-1" data-testid="prepaid-admin-assign-driver"><SelectValue placeholder="Pick driver…"/></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-100 max-h-72">
                    {drivers.map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.name}{d.plate_number ? ` · ${d.plate_number}` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px]">Notes (optional)</Label>
                <Input value={assignNotes} onChange={(e) => setAssignNotes(e.target.value)} placeholder="e.g. weekly Friday run" className="bg-zinc-900 border-zinc-700 mt-1" data-testid="prepaid-admin-assign-notes"/>
              </div>
            </div>
            <Button onClick={assign} disabled={assignBusy || !assignCustomerId || !assignDriverId} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-extrabold" data-testid="prepaid-admin-assign-submit">
              {assignBusy ? <Loader2 className="h-4 w-4 animate-spin"/> : <><Plus className="h-4 w-4 mr-1.5"/>Queue Assignment</>}
            </Button>
          </Card>

          {/* PREPAID CUSTOMERS */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">Prepaid Customers ({customers.length})</div>
            {customers.length === 0 ? (
              <div className="text-xs text-zinc-500 py-3 text-center">No prepaid customers yet. Toggle "prepaid_enabled" on any customer to add them here.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="prepaid-admin-customer-list">
                {customers.map((c) => (
                  <div key={c.id} className="bg-zinc-950 border border-zinc-800 rounded p-2.5 flex items-center gap-2" data-testid={`prepaid-customer-${c.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-zinc-100 truncate">{c.name}</div>
                      <div className="text-[10px] text-zinc-500 truncate">{c.phone || "—"}</div>
                    </div>
                    <div className={`text-sm font-mono font-bold ${(c.prepaid_balance_cents/100) < 10 ? "text-red-300" : "text-emerald-300"}`}>
                      ${(c.prepaid_balance_cents / 100).toFixed(2)}
                    </div>
                    <Button size="sm" onClick={() => setTopupTarget(c)} className="bg-amber-400/20 hover:bg-amber-400/30 text-amber-300 h-7" data-testid={`prepaid-topup-btn-${c.id}`}>
                      Top-up
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* TOP-UP DIALOG */}
          {topupTarget && (
            <Card className="bg-amber-400/10 border-2 border-amber-400/60 p-3 space-y-2" data-testid="prepaid-topup-dialog">
              <div className="flex items-center justify-between">
                <div className="text-xs font-bold text-amber-200">Top-up: {topupTarget.name} · current ${(topupTarget.prepaid_balance_cents/100).toFixed(2)}</div>
                <Button variant="ghost" size="sm" onClick={() => { setTopupTarget(null); setTopupAmount(""); }} className="text-zinc-400 h-7"><X className="h-3.5 w-3.5"/></Button>
              </div>
              <div className="flex gap-2">
                <Input type="number" step="0.01" placeholder="50.00" value={topupAmount} onChange={(e) => setTopupAmount(e.target.value)} className="bg-zinc-950 border-zinc-700" data-testid="prepaid-topup-input"/>
                <Button onClick={topup} disabled={topupBusy || !topupAmount} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold" data-testid="prepaid-topup-submit">
                  {topupBusy ? <Loader2 className="h-4 w-4 animate-spin"/> : "Add to balance"}
                </Button>
              </div>
            </Card>
          )}

          {/* QUEUE */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">Active Queue ({assignments.length})</div>
            {assignments.length === 0 ? (
              <div className="text-xs text-zinc-500 py-3 text-center" data-testid="prepaid-queue-empty">No queued assignments. Use the form above to pre-assign a trip.</div>
            ) : (
              <div className="space-y-1.5" data-testid="prepaid-queue-list">
                {assignments.map((a) => (
                  <div key={a.id} className="bg-zinc-950 border border-zinc-800 rounded p-2 flex items-center gap-3" data-testid={`prepaid-assignment-${a.id}`}>
                    <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded ${a.status === "queued" ? "bg-amber-900/40 text-amber-300" : "bg-blue-900/40 text-blue-300"}`}>
                      {a.status}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-zinc-100 truncate">{a.customer_name}</div>
                      <div className="text-[10px] text-zinc-500 truncate">→ {a.driver_name} · created {a.created_at?.slice(11, 16)}</div>
                    </div>
                    {a.status === "queued" && (
                      <Button variant="ghost" size="sm" onClick={() => cancelAssignment(a.id)} className="text-red-300 hover:bg-red-900/20" data-testid={`prepaid-cancel-${a.id}`}>
                        <Trash2 className="h-3.5 w-3.5"/>
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {customers.length === 0 && (
            <div className="text-xs text-amber-200 bg-amber-900/20 border border-amber-700/30 rounded p-2 flex items-start gap-2" data-testid="prepaid-onboarding-hint">
              <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5"/>
              To enable a customer for prepaid: open Admin → Customers, edit the customer, toggle "Prepaid Enabled" + set the initial balance.
            </div>
          )}
        </>
      )}
    </Card>
  );
}
